import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  registerUser,
  signInUser,
  requestUserEmailConfirmation,
} from '../../../api-client/identity'
import SendPasswordResetRequest from '../../reset-password/views/send-password-reset-request'
import PasswordResetRequestSent from '../../reset-password/views/password-reset-request-sent'
import { Desktop, Mobile } from '../../../components/media'
import { useAuth } from '../../../contexts/auth'
import Loader from '../../../components/loader'
import Modal from '../../../components/modal'
import ResendEmailConfirmationSuccess from '../views/resend-email-confirmation-success'
import ResendEmailConfirmation from '../views/resend-email-confirmation'
import ConfirmEmail from '../views/confirm-email'
import Register from '../views/register'
import Login from '../views/login'

export const VIEWS = {
  Login: 1,
  Register: 2,
  ConfirmEmail: 3,
  ResendEmailConfirmation: 4,
  SendPasswordResetRequest: 5,
  ResetPasswordEmailSent: 6,
  ResendEmailConfirmationSuccess: 7,
}

export default function AuthModal({ onClose, defaultView }) {
  const [, { login }] = useAuth()
  const [view, setView] = useState(defaultView)
  const [submittedForm, setSubmittedForm] = useState({ login: {}, register: {} })

  const {
    mutate: registerMutation,
    isLoading: registeringUser,
    error: registerError,
  } = useMutation({
    mutationFn: registerUser,
    onSuccess: () => setView(VIEWS.ConfirmEmail),
  })

  const { mutate: emailConfiramtionResendMutation } = useMutation(requestUserEmailConfirmation)

  const handleLoginSuccess = ({ accessToken }) => {
    login({ accessToken })

    onClose()
  }

  const { mutate: loginMutation, isLoading: signingUserIn, error: loginError } = useMutation(
    {
      mutationFn: signInUser,
      onSuccess: handleLoginSuccess,
    },
  )

  const handleLoginSubmit = (form) => {
    setSubmittedForm((prev) => ({ ...prev, login: form }))
    loginMutation(form)
  }

  const handleRegisterSubmit = (form) => {
    setSubmittedForm((prev) => ({ ...prev, register: form }))
    registerMutation(form)
  }

  const handleEmailConfirmationResend = useCallback(
    (form) => {
      emailConfiramtionResendMutation(
        {
          email: submittedForm[form].email,
          password: submittedForm[form].password,
        },
        { onSuccess: () => setView(VIEWS.ResendEmailConfirmationSuccess) },
      )
    },
    [emailConfiramtionResendMutation, submittedForm],
  )

  const handleLogin = useCallback(() => setView(VIEWS.Login), [])
  const handleRegister = useCallback(() => setView(VIEWS.Register), [])
  const handleResetPassword = useCallback(() => setView(VIEWS.SendPasswordResetRequest), [])
  const handleEmailConfirmationPending = useCallback(() => {
    emailConfiramtionResendMutation(
      {
        email: submittedForm.login.email,
        password: submittedForm.login.password,
      },
      { onSuccess: () => setView(VIEWS.ResendEmailConfirmation) },
    )
  }, [emailConfiramtionResendMutation, submittedForm])
  const handlePasswordResetRequestSubmit = useCallback(
    () => setView(VIEWS.ResetPasswordEmailSent),
    [],
  )

  return (
    <>
      <Desktop>
        <Modal onClose={onClose}>
          <div className='wrapper'>
            {view === VIEWS.Login && (
              <Login
                onSubmit={handleLoginSubmit}
                disabled={signingUserIn}
                serverErrors={loginError}
                onRegister={handleRegister}
                onResetPassword={handleResetPassword}
                onEmailConfirmationPending={handleEmailConfirmationPending}
              />
            )}
            {view === VIEWS.Register && (
              <Register
                submittedForm={submittedForm.register}
                onSubmit={handleRegisterSubmit}
                disabled={registeringUser}
                serverErrors={registerError}
                onLogin={handleLogin}
                onClose={onClose}
              />
            )}
            {view === VIEWS.ConfirmEmail && (
              <ConfirmEmail
                onResend={() => handleEmailConfirmationResend('register')}
                onClose={onClose}
                emailAddress={submittedForm.register.email}
              />
            )}
            {view === VIEWS.ResendEmailConfirmation && (
              <ResendEmailConfirmation
                onResend={() => handleEmailConfirmationResend('login')}
                email={submittedForm.login.email}
              />
            )}
            {view === VIEWS.ResendEmailConfirmationSuccess && (
              <ResendEmailConfirmationSuccess onClose={onClose} />
            )}
            {view === VIEWS.SendPasswordResetRequest && (
              <SendPasswordResetRequest onSubmit={handlePasswordResetRequestSubmit} />
            )}
            {view === VIEWS.ResetPasswordEmailSent && (
              <PasswordResetRequestSent onClose={onClose} />
            )}
          </div>
        </Modal>
      </Desktop>
      <Mobile>
        <Modal onClose={onClose}>
          <div className='wrapper mobile'>
            {view === VIEWS.Login && (
              <Login
                onClose={onClose}
                onSubmit={handleLoginSubmit}
                disabled={signingUserIn}
                serverErrors={loginError}
                onRegister={handleRegister}
                onResetPassword={handleResetPassword}
                onEmailConfirmationPending={handleEmailConfirmationPending}
              />
            )}
            {view === VIEWS.Register && (
              <Register
                submittedForm={submittedForm.register}
                onClose={onClose}
                onSubmit={handleRegisterSubmit}
                disabled={registeringUser}
                serverErrors={registerError}
                onLogin={handleLogin}
              />
            )}
            {view === VIEWS.ConfirmEmail && (
              <ConfirmEmail
                onClose={onClose}
                onResend={() => handleEmailConfirmationResend('register')}
                emailAddress={submittedForm.register.email}
              />
            )}
            {view === VIEWS.ResendEmailConfirmation && (
              <ResendEmailConfirmation
                onResend={() => handleEmailConfirmationResend('login')}
                email={submittedForm.login.email}
              />
            )}
            {view === VIEWS.ResendEmailConfirmationSuccess && (
              <ResendEmailConfirmationSuccess onClose={onClose} />
            )}
            {view === VIEWS.SendPasswordResetRequest && (
              <SendPasswordResetRequest onSubmit={handlePasswordResetRequestSubmit} />
            )}
            {view === VIEWS.ResetPasswordEmailSent && (
              <PasswordResetRequestSent onClose={onClose} />
            )}
          </div>
        </Modal>
      </Mobile>

      {(signingUserIn || registeringUser) && <Loader delay={500} fullPage />}

      <style jsx>{`
        .wrapper {
          background: #fff;

          &.mobile {
            width: 100%;
            height: 100%;
            position: fixed;
            top: 5.5rem;
            overflow: scroll;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </>
  )
}
